import PropTypes from "prop-types";
import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";

// store
import useBrainStore from "@/store/_brainEmStore";
import useLinkStore from "@/store/_linkStore";
import { organismImgData } from "@/data/OrganismImgData";

// styles
import "./ExportHighlights.css";

// -----------------------------------------------------------------------------

export default function ExportHighlights({ isLinkMode, onComplete, imgStyle }) {
	// Determine which store hook to use
	const useStore = isLinkMode ? useLinkStore : useBrainStore;
	// Select the organism state
	const organism = useStore((state) => state.organism);

	// Destructure appropriate data based on imgStyle
	// img file and class are enough, no other data needed for png export
	const {
		imageSciLarge,
		classNameSciLarge,
		imagePhotoLarge,
		classNamePhotoLarge,
	} = organismImgData[organism];

	// Determine the specific image and class to use based on imgStyle prop
	let imageToUse;
	let classToUse;

	if (imgStyle === "sciIllustration") {
		imageToUse = imageSciLarge;
		classToUse = classNameSciLarge;
	} else {
		imageToUse = imagePhotoLarge;
		classToUse = classNamePhotoLarge;
	}

	// Keep these hardcoded for now, TODO
	const yearsToEmulation = 8;
	const totalCost = "1.20 B";

	const cardRef = useRef(null);

	// ------------------------------------------------------------------------------

	const [exportError, setExportError] = useState(null); // Track errors
	const [isFetchingCardId, setIsFetchingCardId] = useState(true); // Track card id fetching state
	const [cardId, setCardId] = useState(null); // Keep track of fetched ID
	const [portalElement, setPortalElement] = useState(null);

	// ------------------------------------------------------------------------------

	// EFFECT 1 – Create a portal container on mount, and clean up
	useEffect(() => {
		// Create a div that will serve as our portal container
		const el = document.createElement("div");
		el.className = "hidden-container";
		document.body.appendChild(el);

		setPortalElement(el);

		// Clean up on unmount
		return () => {
			if (document.body.contains(el)) {
				document.body.removeChild(el);
			}
		};
	}, []); // Runs only once on mount

	// ---------------------------------------------------------------------------------

	// EFFECT 2 – Fetch Card ID when the portal is ready
	useEffect(() => {
		// Only run if the portal element exists and we haven't successfully fetched yet
		if (!portalElement || !isFetchingCardId) return;

		let isMounted = true; // Prevent state updates on unmounted component

		async function fetchCardId() {
			let retries = 3;
			let success = false;

			while (retries > 0 && !success && isMounted) {
				try {
					const res = await fetch("/api/increment-card-id", { method: "POST" });

					// Handle response error
					if (!res.ok) {
						// Try to get error details, default to status text
						let errorMsg = res.statusText;
						try {
							const errBody = await res.json();
							errorMsg = errBody.error || errorMsg;
						} catch (_) {
							// Ignore if response is not JSON
						}
						// throw to catch block
						throw new Error(`API Error (${res.status}): ${errorMsg}`);
					}

					// Handle successful response
					const data = await res.json();
					if (isMounted) {
						setCardId(data.cardId);
						setIsFetchingCardId(false); // Mark fetching as complete
						setExportError(null); // Clear previous errors
						success = true;
					}

					// ---------------------------------------------------------------------------
				} catch (error) {
					console.warn(
						`Error fetching card ID (attempt ${4 - retries}):`,
						error,
					);

					retries--;

					if (retries === 0 && isMounted) {
						// All retries failed
						console.error(
							"Error fetching card ID after multiple attempts.",
							error,
						); // Sentry will capture this

						setExportError(
							error.message ||
								"Failed to fetch Card ID after multiple attempts.",
						);
						setIsFetchingCardId(false); // Stop fetching
					} else if (isMounted) {
						// Wait before retrying
						await new Promise((resolve) =>
							setTimeout(resolve, 200 * (4 - retries)),
						);
					}
				}
			}
		}

		// Give images/fonts time to potentially load before fetching ID
		// const timer = setTimeout(fetchCardId, 50);
		fetchCardId(); // Call directly

		return () => {
			isMounted = false; // Cleanup flag
			// clearTimeout(timer);
		};
		// Depend on portalElement to ensure it exists before fetching
	}, [portalElement, isFetchingCardId]);

	// ---------------------------------------------------------------------------------

	// EFFECT 3 –Export to PNG when Card ID is available and rendered
	useEffect(() => {
		// Only run if:
		// - Portal exists
		// - We are NOT fetching the ID anymore
		// - We have a valid cardId OR there was an error fetching it (still need to call onComplete)
		// - The cardRef is attached
		if (
			!portalElement ||
			isFetchingCardId ||
			(cardId === null && !exportError) ||
			!cardRef.current
		) {
			return;
		}

		let isMounted = true;

		async function exportCard() {
			// If there was an error fetching the ID, skip PNG generation but call onComplete
			if (exportError) {
				console.error(
					"Skipping PNG export due to Card ID fetch error:",
					exportError,
				);
				if (isMounted) onComplete(exportError); // Pass error to parent
				return;
			}

			// Proceed with PNG export
			try {
				let dataUrl = "";
				const minDataLength = 400000; // Adjusted based on observed successful lengths
				const maxAttempts = 10;
				let attempts = 0;

				while (dataUrl.length < minDataLength && attempts < maxAttempts) {
					// console.error(`Attempt ${attempts + 1} to generate PNG...`);

					dataUrl = await toPng(cardRef.current, {
						pixelRatio: 3,
						cacheBust: true,
					});

					// console.error(
					// 	`Attempt ${attempts + 1}: dataUrl length = ${dataUrl.length}`,
					// );

					attempts++;

					if (dataUrl.length < minDataLength && attempts < maxAttempts) {
						// Optional: Add a small delay before retrying
						await new Promise((resolve) => setTimeout(resolve, 50));
					}
				}

				if (dataUrl.length < minDataLength) {
					throw new Error(
						`Failed to generate adequate PNG after ${maxAttempts} attempts. Last dataUrl length: ${dataUrl.length}`,
					);
				}

				// Create a temporary download link and trigger download
				const link = document.createElement("a");
				link.download = "brainEm-highlights-card.png";
				link.href = dataUrl;
				link.click();
				link.remove(); // Clean up the link element

				if (isMounted) onComplete(); // Notify parent: Success

				// -------------------------------------------------------------------------------
			} catch (error) {
				console.error("Error exporting card to PNG:", error);
				if (isMounted) {
					setExportError("Failed to generate PNG image."); // Set specific export error
					onComplete(error); // Notify parent: Failure
				}
			}
		}

		// Use requestAnimationFrame or minimal timeout
		// Wait for the DOM to update with the cardId before exporting
		// requestAnimationFrame is generally preferred over setTimeout(0) for DOM updates
		const rafId = requestAnimationFrame(() => {
			// Small timeout might still be needed for complex rendering/fonts
			// const timer = setTimeout(exportCard, 500);
			// return () => clearTimeout(timer);
			exportCard(); // Call exportCard directly
		});

		return () => {
			isMounted = false;
			cancelAnimationFrame(rafId);
			// Ensure any pending timeout inside requestAnimationFrame is also cleared if possible
			// The return from RAF callback handles its timer.
		};
		// Depend on cardId and exportError to trigger when ID is ready or fetch failed
		// also on imageToUse if it can change and requires reloading
	}, [portalElement, isFetchingCardId, cardId, exportError, onComplete]);

	// -----------------------------------------------------------------------------

	// Render nothing until the portal container is ready
	if (!portalElement) return null;

	// Render the card inside the portal
	return createPortal(
		<div ref={cardRef} className="highlights-card">
			{/* IMG */}
			<div className="card-img-container">
				{/* biome-ignore lint/a11y/useAltText: <explanation> */}
				<img src={imageToUse} className={classToUse} />
			</div>

			<div className="data-container">
				{/* ORGANISM */}
				<div className="organism-highlight-container">
					{/* Heading */}
					<p className="highlight-heading">Organism</p>
					{/* Big value - Use selected organism state */}
					<p className="highlight-value">{organism}</p>
				</div>

				{/* TIME TO EMULATION */}
				<div className="numerical-highlight-container border-right">
					{/* Heading */}
					<p className="highlight-heading">Time to Emulation</p>
					{/* Big value */}
					<p className="highlight-value">{yearsToEmulation} years</p>
				</div>

				{/* TOTAL COST */}
				<div className="numerical-highlight-container">
					{/* Heading */}
					<p className="highlight-heading">Total Cost in USD</p>
					{/* Big value */}
					<p className="highlight-value">{totalCost}</p>
				</div>
			</div>

			{/* Card footer */}
			<div className="card-footer">
				<p>brainem.co</p>
				<p>Card ID: {cardId !== null ? cardId : "Export again"}</p>
			</div>
		</div>,
		portalElement,
	);
}

// -----------------------------------------------------------------------------

ExportHighlights.propTypes = {
	isLinkMode: PropTypes.bool.isRequired,
	onComplete: PropTypes.func.isRequired,
	imgStyle: PropTypes.string.isRequired,
};
